import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Button, Form, Icon, IconButton, Spinner,
} from '@openedx/paragon';
import { Close } from '@openedx/paragon/icons';
import { useIntl } from '@edx/frontend-platform/i18n';

import {
  analyzeUnitForCondenser,
  createSkillBuilderSourceSession,
  createUnitCondenserPlan,
  getUnitCondenserAnalysisJob,
  getUnitCondenserAnalysisResult,
  getUnitCondenserGenerationResult,
  getUnitCondenserDiscussion,
  getUnitCondenserPlan,
  getUnitCondenserPlanForAnalysis,
  getUnitCondenserPlanRevisions,
  listSkillBuilderSourceSessionSources,
  lockUnitCondenserPlan,
  forkUnitCondenserPlan,
  uploadSkillBuilderSource,
  SkillBuilderSourceStatus,
  UnitCondenserPlanAction,
  UnitCondenserAnalysisResult,
  UnitCondenserGenerationResult,
  UnitCondenserPlan,
  UnitCondenserPlanResponse,
  UnitCondenserConcept,
  UnitCondenserConceptEvidence,
  updateUnitCondenserPlan,
  submitUnitCondenserGeneration,
  postUnitCondenserDiscussion,
  UnitCondenserDiscussionResponse,
  UnitCondenserDiscussionMetadata,
  UnitCondenserRecentGenerationContext,
} from '../data/api';
import useSkillBuilderJob from '../hooks/useSkillBuilderJob';
import messages from '../messages';
import MarkdownMessage from './MarkdownMessage';
import UnitCondenserResult from './UnitCondenserResult';

const STEPS = ['Analyze', 'Sources & constraints', 'Review plan', 'Approve & generate', 'Review result'];
const INITIAL_PLAN_WORD_BUDGET = 800;
const SOURCE_LOADING_STATUSES = ['queued', 'running', 'cancel_requested'];

const createClientMessageId = () => (
  typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`
);

const proposedWordCount = (concepts: UnitCondenserConcept[]) => concepts.reduce(
  (sum, concept) => sum + concept.proposed_words,
  0,
);

const parsePositiveInteger = (value: string): number | null => {
  if (!/^\d+$/.test(value)) { return null; }
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
};

const conceptEvidence = (concept: UnitCondenserConcept): UnitCondenserConceptEvidence[] => {
  if (concept.importance_reasons?.length) { return concept.importance_reasons; }
  const reasons: UnitCondenserConceptEvidence[] = [{
    kind: 'unit_core',
    label: 'Core concept in this unit',
    detail: 'This concept appears as an instructional section in the selected unit.',
    source_ids: [],
  }];
  if (['required', 'supporting'].includes(concept.assessment_importance)) {
    reasons.push({
      kind: 'assessment',
      label: 'Found in assessment',
      detail: concept.assessment_rationale,
      source_ids: [],
    });
  }
  if (concept.design_importance && concept.design_importance !== 'not provided') {
    reasons.push({
      kind: 'design_document',
      label: 'Found in design document',
      detail: 'Referenced in a selected supplemental source.',
      source_ids: [],
    });
  }
  return reasons;
};

const conceptWordSummary = (concept: UnitCondenserConcept) => {
  const originalWords = concept.original_words.toLocaleString();
  const proposedWords = concept.proposed_words.toLocaleString();
  if (!concept.original_words) {
    return `${proposedWords} proposed words`;
  }
  if (concept.action === 'keep') {
    return `${originalWords} original words -> ${proposedWords} proposed words · unchanged`;
  }
  const difference = concept.original_words - concept.proposed_words;
  const change = difference >= 0
    ? `${difference.toLocaleString()} cut (${Math.round((difference / concept.original_words) * 100)}%)`
    : `${Math.abs(difference).toLocaleString()} added`;
  return `${originalWords} original words -> ${proposedWords} proposed words · ${change}`;
};

const approvalWordSummary = (concept: UnitCondenserConcept) => {
  const proposedWords = concept.proposed_words.toLocaleString();
  if (concept.original_words > concept.proposed_words) {
    return `${proposedWords} words (reduce from ${concept.original_words.toLocaleString()} words)`;
  }
  return `${proposedWords} words`;
};

interface UnitCondenserPlanShellProps {
  analysisJobId?: string | null;
}

const UnitCondenserPlanShell: React.FC<UnitCondenserPlanShellProps> = ({
  analysisJobId: initialAnalysisJobId = null,
}) => {
  const intl = useIntl();
  const [activeStep, setActiveStep] = useState(0);
  const [unitUrl, setUnitUrl] = useState('');
  const [analyzedUrl, setAnalyzedUrl] = useState('');
  const [analysisJobId, setAnalysisJobId] = useState<string | null>(initialAnalysisJobId);
  const [analysisStatus, setAnalysisStatus] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<UnitCondenserAnalysisResult | null>(null);
  const [response, setResponse] = useState<UnitCondenserPlanResponse | null>(null);
  const [plan, setPlan] = useState<UnitCondenserPlan | null>(null);
  const [recentGenerationContext, setRecentGenerationContext] = useState<
  UnitCondenserRecentGenerationContext | null
  >(null);
  const [target, setTarget] = useState(String(INITIAL_PLAN_WORD_BUDGET));
  const [targetError, setTargetError] = useState<string | null>(null);
  const [proposedWordInputs, setProposedWordInputs] = useState<Record<string, string>>({});
  const [instructions, setInstructions] = useState('');
  const [sourceSessionId, setSourceSessionId] = useState<string | null>(null);
  const [sources, setSources] = useState<SkillBuilderSourceStatus[]>([]);
  const [selectedSourceIds, setSelectedSourceIds] = useState<string[]>([]);
  const [uploadingSourceNames, setUploadingSourceNames] = useState<string[]>([]);
  const [revisions, setRevisions] = useState<UnitCondenserPlan[]>([]);
  const [uploadingSources, setUploadingSources] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [generationJobId, setGenerationJobId] = useState<string | null>(null);
  const [generationResult, setGenerationResult] = useState<UnitCondenserGenerationResult | null>(null);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [generationResultRetry, setGenerationResultRetry] = useState(0);
  const [discussion, setDiscussion] = useState<UnitCondenserDiscussionResponse | null>(null);
  const [discussionInput, setDiscussionInput] = useState('');
  const [discussionError, setDiscussionError] = useState<string | null>(null);
  const [discussionSending, setDiscussionSending] = useState(false);
  const [discussionReloading, setDiscussionReloading] = useState(false);
  const analysisGeneration = useRef(0);
  const analysisIdempotencyKey = useRef<string | null>(null);
  const generationIdempotencyKey = useRef<string | null>(null);
  const discussionMessageId = useRef<string | null>(null);
  const discussionPendingMessage = useRef<string | null>(null);
  const uploadGeneration = useRef(0);
  const { job: generationJob, error: generationPollingError } = useSkillBuilderJob(generationJobId, 3000);

  const sourceProcessing = sources.some(source => ['queued', 'running', 'cancel_requested'].includes(source.status));
  const unavailableSelectedSources = selectedSourceIds.some(
    sourceId => !sources.some(source => source.source_id === sourceId && source.status === 'completed' && source.extracted),
  );
  const isDirty = Boolean(response && plan && JSON.stringify(response.plan) !== JSON.stringify(plan));
  const proposedWordError = (concept: UnitCondenserConcept): string | null => {
    if (!['keep', 'shorten'].includes(concept.action)) { return null; }
    const value = proposedWordInputs[concept.id] ?? String(concept.proposed_words);
    return parsePositiveInteger(value) === null ? 'Enter a whole number greater than 0.' : null;
  };
  const hasInvalidProposedWordInput = Boolean(plan?.concepts.some(concept => proposedWordError(concept)));
  const generationPending = Boolean(
    generationJobId && (!generationJob || ['queued', 'running'].includes(generationJob.status)),
  );
  const analysisInProgress = Boolean(
    saving || (analysisJobId && analysisStatus && !['completed', 'failed', 'cancelled'].includes(analysisStatus)),
  );
  let analysisButtonLabel = 'Analyze Unit';
  if (analysisStatus === 'failed') { analysisButtonLabel = 'Retry analysis'; }
  if (analysisInProgress) { analysisButtonLabel = 'Analyzing unit...'; }

  useEffect(() => {
    if (!response?.job_id) {
      setDiscussion(null);
      setDiscussionInput('');
      return undefined;
    }
    let active = true;
    setDiscussionError(null);
    getUnitCondenserDiscussion(response.job_id)
      .then(nextDiscussion => { if (active) { setDiscussion(nextDiscussion); } })
      .catch(() => { if (active) { setDiscussionError('Unable to load the plan discussion.'); } });
    return () => { active = false; };
  }, [plan?.revision, response?.job_id]);

  const analyze = async () => {
    const generation = analysisGeneration.current + 1;
    analysisGeneration.current = generation;
    uploadGeneration.current += 1;
    setSaving(true);
    setError(null);
    setPlan(null);
    setProposedWordInputs({});
    setResponse(null);
    setAnalysis(null);
    setSourceSessionId(null);
    setSources([]);
    setSelectedSourceIds([]);
    setUploadingSourceNames([]);
    setRevisions([]);
    setAnalyzedUrl('');
    setAnalysisJobId(null);
    setActiveStep(0);
    if (analysisJobId && analysisStatus === 'failed') {
      analysisIdempotencyKey.current = null;
    }
    if (unitUrl.trim() !== analyzedUrl) {
      analysisIdempotencyKey.current = null;
    }
    if (!analysisIdempotencyKey.current) {
      analysisIdempotencyKey.current = typeof globalThis.crypto?.randomUUID === 'function'
        ? globalThis.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
    try {
      const job = await analyzeUnitForCondenser(unitUrl.trim(), analysisIdempotencyKey.current);
      if (generation !== analysisGeneration.current) { return; }
      setAnalysisJobId(job.job_id);
      setAnalysisStatus(job.status);
      setAnalyzedUrl(unitUrl.trim());
      setResponse(null);
      setPlan(null);
    } catch {
      if (generation === analysisGeneration.current) {
        setError('Unable to analyze this unit. Check the learner-view URL and try again.');
      }
    } finally {
      if (generation === analysisGeneration.current) { setSaving(false); }
    }
  };

  useEffect(() => {
    if (!analysisJobId) { return undefined; }
    let active = true;
    let running = true;
    let pollInFlight = false;
    const poll = async () => {
      if (pollInFlight) { return; }
      pollInFlight = true;
      try {
        const job = await getUnitCondenserAnalysisJob(analysisJobId);
        if (!active) { return; }
        setAnalysisStatus(job.status);
        if (job.status === 'completed') {
          const result = await getUnitCondenserAnalysisResult(analysisJobId);
          if (!active) { return; }
          setAnalysis(result);
          const existing = await getUnitCondenserPlanForAnalysis(analysisJobId).catch((requestError: any) => {
            if (requestError?.response?.status === 404) { return null; }
            throw requestError;
          });
          if (active && existing) {
            setResponse(existing);
            setPlan(existing.plan);
            setProposedWordInputs({});
            setRecentGenerationContext(existing.recent_generation_context || null);
            setTarget(String(existing.plan.target_word_count));
            setInstructions(existing.plan.instructions);
            setSourceSessionId(existing.plan.source_session_id);
            setSelectedSourceIds(existing.plan.selected_source_ids);
            setRevisions(existing.revisions || []);
            setActiveStep(2);
          }
          running = false;
        } else if (job.status === 'failed' || job.status === 'cancelled') {
          running = false;
          setError(job.error || 'Unit analysis failed. Check the URL or retry analysis.');
        }
      } catch {
        if (active) { setError('Unable to retrieve analysis status.'); }
      } finally {
        pollInFlight = false;
      }
    };
    poll();
    const intervalId = window.setInterval(() => { if (running) { poll(); } }, 3000);
    return () => { active = false; window.clearInterval(intervalId); };
  }, [analysisJobId]);

  useEffect(() => {
    if (!sourceSessionId) {
      setSources([]);
      return undefined;
    }
    let active = true;
    let refreshInFlight = false;
    const refresh = async () => {
      if (refreshInFlight) { return; }
      try {
        refreshInFlight = true;
        const nextSources = await listSkillBuilderSourceSessionSources(sourceSessionId);
        if (active) {
          setSources(current => nextSources.map(source => ({
            ...source,
            // Preserve the local filename when the status endpoint omits it.
            filename: source.filename || current.find(existing => existing.source_id === source.source_id)?.filename,
          })));
          setSelectedSourceIds(current => current.filter(sourceId => (
            nextSources.some(source => (
              source.source_id === sourceId && !['failed', 'cancelled', 'unavailable'].includes(source.status)
            ))
          )));
        }
      } catch {
        if (active) { setError('Unable to retrieve supplemental source status.'); }
      } finally {
        refreshInFlight = false;
      }
    };
    refresh();
    if (!sourceProcessing) { return () => { active = false; }; }
    const interval = window.setInterval(refresh, 5000);
    return () => { active = false; window.clearInterval(interval); };
  }, [sourceProcessing, sourceSessionId]);

  useEffect(() => {
    if (!response?.job_id) { return undefined; }
    let active = true;
    getUnitCondenserPlanRevisions(response.job_id)
      .then(history => { if (active) { setRevisions(history.revisions); } })
      .catch(() => undefined);
    return () => { active = false; };
  }, [plan?.revision, response?.job_id]);

  useEffect(() => {
    if (!generationJobId || !generationJob) { return undefined; }
    if (generationJob.status === 'failed' || generationJob.status === 'cancelled') {
      setGenerationError(generationJob.error || 'Unit generation failed. Retry from this locked revision.');
      return undefined;
    }
    if (generationJob.status !== 'completed' || generationResult) { return undefined; }
    let active = true;
    getUnitCondenserGenerationResult(generationJobId)
      .then(result => {
        if (active) {
          setGenerationResult(result);
          setActiveStep(4);
        }
      })
      .catch(() => { if (active) { setGenerationError('Unable to retrieve the generated unit result.'); } });
    return () => { active = false; };
  }, [generationJob, generationJobId, generationResult, generationResultRetry]);

  const uploadSources = async (files: FileList | null) => {
    if (!files?.length) { return; }
    const selectedFiles = Array.from(files);
    const generation = uploadGeneration.current + 1;
    uploadGeneration.current = generation;
    setUploadingSourceNames(current => Array.from(new Set([...current, ...selectedFiles.map(file => file.name)])));
    setUploadingSources(true);
    setError(null);
    try {
      let sessionId = sourceSessionId;
      if (!sessionId) {
        const session = await createSkillBuilderSourceSession();
        if (generation !== uploadGeneration.current) { return; }
        sessionId = session.job_id;
        setSourceSessionId(sessionId);
      }
      const successfulUploads: SkillBuilderSourceStatus[] = [];
      for (const file of selectedFiles) {
        if (generation !== uploadGeneration.current) { return; }
        try {
          // Source-session updates must remain serialized to avoid manifest races.
          // eslint-disable-next-line no-await-in-loop
          const source = await uploadSkillBuilderSource(sessionId as string, file);
          successfulUploads.push({
            source_id: source.source_id,
            job_id: source.job_id,
            status: source.status,
            extracted: false,
            filename: file.name,
          });
        } catch {
          if (generation === uploadGeneration.current) {
            setError(`Unable to upload ${file.name}. Other selected files were kept.`);
          }
        }
      }
      if (generation === uploadGeneration.current) {
        setSources(current => {
          const uploadedById = new Map(successfulUploads.map(source => [source.source_id, source]));
          return [
            ...current.map(source => {
              const uploaded = uploadedById.get(source.source_id);
              return uploaded ? { ...source, ...uploaded, filename: source.filename || uploaded.filename } : source;
            }),
            ...successfulUploads.filter(source => !current.some(existing => existing.source_id === source.source_id)),
          ];
        });
        setSelectedSourceIds(current => [
          ...current,
          ...successfulUploads
            .map(source => source.source_id)
            .filter(sourceId => !current.includes(sourceId)),
        ]);
      }
    } catch {
      if (generation === uploadGeneration.current) {
        setError('Unable to create a supplemental source session.');
      }
    } finally {
      if (generation === uploadGeneration.current) {
        setUploadingSources(false);
        setUploadingSourceNames(current => current.filter(name => !selectedFiles.some(file => file.name === name)));
      }
    }
  };

  const removeSource = (sourceId: string) => {
    setSelectedSourceIds(current => current.filter(selectedId => selectedId !== sourceId));
  };

  const create = async () => {
    if (unavailableSelectedSources) {
      setError('Wait for selected supplemental sources to finish processing before creating the plan.');
      return;
    }
    const targetWordCount = parsePositiveInteger(target);
    if (targetWordCount === null) {
      setTargetError('Enter a positive whole number greater than 0 before continuing.');
      return;
    }
    setTargetError(null);
    setSaving(true);
    setError(null);
    try {
      const created = await createUnitCondenserPlan({
        analysis_job_id: analysisJobId as string,
        expected_revision: 0,
        target_word_count: targetWordCount,
        budget_override_reason: null,
        instructions,
        source_session_id: sourceSessionId,
        source_ids: selectedSourceIds.filter(sourceId => (
          sources.some(source => source.source_id === sourceId && source.status === 'completed' && source.extracted)
        )),
      });
      setResponse(created);
      setPlan(created.plan);
      setProposedWordInputs({});
      setRecentGenerationContext(created.recent_generation_context || null);
      setRevisions(created.revisions || []);
      setActiveStep(2);
    } catch {
      setError('Unable to create the plan. Reload the analysis and try again.');
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    if (!response || !plan) { return; }
    if (hasInvalidProposedWordInput) {
      setError('Enter a whole number for every Proposed words field before updating the plan.');
      return;
    }
    setSaving(true);
    setError(null);
    setConflict(false);
    try {
      const saved = await updateUnitCondenserPlan(response.job_id, plan, plan.concepts);
      setResponse(saved);
      setPlan(saved.plan);
      setProposedWordInputs({});
      setRecentGenerationContext(saved.recent_generation_context || recentGenerationContext);
      setTarget(String(saved.plan.target_word_count));
      setInstructions(saved.plan.instructions);
    } catch (requestError: any) {
      if (requestError?.response?.status === 409) {
        setConflict(true);
        setError('This plan changed elsewhere. Your edits are preserved; reload the latest revision before saving.');
      } else {
        setError('Unable to save this plan revision. Your edits are preserved.');
      }
    } finally {
      setSaving(false);
    }
  };

  const lock = async (): Promise<UnitCondenserPlanResponse | null> => {
    if (!response || !plan) { return null; }
    if (hasInvalidProposedWordInput) {
      setError('Enter a whole number for every Proposed words field before accepting the plan.');
      return null;
    }
    if (isDirty) {
      setError('Update Plan before accepting these edits.');
      return null;
    }
    setSaving(true);
    setError(null);
    try {
      const locked = await lockUnitCondenserPlan(response.job_id, plan.revision);
      setResponse(locked);
      setPlan(locked.plan);
      setProposedWordInputs({});
      setRecentGenerationContext(locked.recent_generation_context || recentGenerationContext);
      setTarget(String(locked.plan.target_word_count));
      setInstructions(locked.plan.instructions);
      return locked;
    } catch {
      setError('Unable to lock this revision. Reload the latest plan and try again.');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const newDraft = async () => {
    if (!response || !plan) { return; }
    setSaving(true);
    setError(null);
    try {
      const draft = await forkUnitCondenserPlan(response.job_id, plan.revision);
      setResponse(draft);
      setPlan(draft.plan);
      setProposedWordInputs({});
      setRecentGenerationContext(draft.recent_generation_context || null);
      setTarget(String(draft.plan.target_word_count));
      setInstructions(draft.plan.instructions);
      setSourceSessionId(draft.plan.source_session_id);
      setSelectedSourceIds(draft.plan.selected_source_ids);
      setRevisions(draft.revisions || []);
      setGenerationJobId(null);
      setGenerationResult(null);
      setGenerationError(null);
      setGenerationResultRetry(0);
      generationIdempotencyKey.current = null;
      setActiveStep(2);
    } catch {
      setError('Unable to create a new draft from this revision. Reload the plan and try again.');
    } finally {
      setSaving(false);
    }
  };

  const generate = async (
    generationResponse: UnitCondenserPlanResponse | null = response,
    generationPlan: UnitCondenserPlan | null = plan,
  ) => {
    if (!generationResponse || !generationPlan || generationPlan.state !== 'locked') { return; }
    setActiveStep(3);
    setSaving(true);
    setGenerationError(null);
    setGenerationResult(null);
    setGenerationResultRetry(0);
    const canReuseGenerationKey = Boolean(
      generationJobId && (!generationJob || ['queued', 'running'].includes(generationJob.status)),
    );
    if (!canReuseGenerationKey) {
      generationIdempotencyKey.current = null;
    }
    if (!generationIdempotencyKey.current) {
      generationIdempotencyKey.current = typeof globalThis.crypto?.randomUUID === 'function'
        ? globalThis.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
    try {
      const job = await submitUnitCondenserGeneration(
        generationResponse.job_id,
        generationPlan.revision,
        generationIdempotencyKey.current,
      );
      setGenerationJobId(job.job_id);
    } catch {
      setGenerationError('Unable to start generation for this locked revision.');
    } finally {
      setSaving(false);
    }
  };

  const acceptAndGenerate = async () => {
    const locked = await lock();
    if (locked) { await generate(locked, locked.plan); }
  };

  const updateConceptDetails = (
    conceptId: string,
    changes: {
      action?: UnitCondenserPlanAction;
      merge_target_id?: string | null;
      rationale?: string;
      proposed_words?: number;
      author_override?: string | null;
    },
  ) => {
    setPlan((current) => {
      if (!current) { return current; }
      const concepts = current.concepts.map((concept) => (
        concept.id === conceptId ? { ...concept, ...changes } : concept
      ));
      const total = proposedWordCount(concepts);
      return {
        ...current,
        concepts,
        target_word_count: total,
        proposed_word_count: total,
      };
    });
  };

  const canNavigateTo = (step: number) => {
    if (step === 0) { return true; }
    if (step === 1) { return Boolean(analysis && analysisStatus === 'completed'); }
    if (step === 2 || step === 3) { return Boolean(plan && response); }
    return Boolean(generationResult);
  };

  const goToStep = (step: number) => {
    if (canNavigateTo(step)) { setActiveStep(step); }
  };

  const reset = () => {
    setActiveStep(0);
    setUnitUrl('');
    setAnalyzedUrl('');
    setAnalysisJobId(null);
    setAnalysisStatus(null);
    setAnalysis(null);
    setResponse(null);
    setPlan(null);
    setRecentGenerationContext(null);
    setProposedWordInputs({});
    setTarget(String(INITIAL_PLAN_WORD_BUDGET));
    setTargetError(null);
    setInstructions('');
    setSourceSessionId(null);
    setSources([]);
    setSelectedSourceIds([]);
    setRevisions([]);
    setGenerationJobId(null);
    setGenerationResult(null);
    setGenerationError(null);
    setError(null);
    setDiscussion(null);
    setDiscussionInput('');
    setDiscussionError(null);
    setConflict(false);
    discussionMessageId.current = null;
    discussionPendingMessage.current = null;
    generationIdempotencyKey.current = null;
  };

  const reloadDiscussion = async () => {
    if (!response?.job_id) { return; }
    setDiscussionReloading(true);
    setDiscussionError(null);
    try {
      const latestPlanResponse = await getUnitCondenserPlan(response.job_id);
      setResponse(latestPlanResponse);
      setPlan(latestPlanResponse.plan);
      setProposedWordInputs({});
      setRecentGenerationContext(latestPlanResponse.recent_generation_context || null);
      setTarget(String(latestPlanResponse.plan.target_word_count));
      setInstructions(latestPlanResponse.plan.instructions);
      setSourceSessionId(latestPlanResponse.plan.source_session_id);
      setSelectedSourceIds(latestPlanResponse.plan.selected_source_ids);
      setRevisions(latestPlanResponse.revisions || []);
      const latestDiscussion = await getUnitCondenserDiscussion(response.job_id);
      setDiscussion(latestDiscussion);
      setConflict(false);
    } catch {
      setDiscussionError('Unable to reload the latest draft. Your message is still preserved; try again.');
    } finally {
      setDiscussionReloading(false);
    }
  };

  const sendDiscussionMessage = async () => {
    if (!response || !plan || !discussionInput.trim()) { return; }
    setDiscussionSending(true);
    setDiscussionError(null);
    const message = discussionInput.trim();
    const clientMessageId = discussionMessageId.current && discussionPendingMessage.current === message
      ? discussionMessageId.current
      : createClientMessageId();
    discussionMessageId.current = clientMessageId;
    discussionPendingMessage.current = message;
    try {
      const nextDiscussion = await postUnitCondenserDiscussion(response.job_id, {
        expected_revision: plan.revision,
        expected_discussion_version: discussion?.discussion_version || 0,
        client_message_id: clientMessageId,
        message,
        draft_plan: isDirty ? plan : undefined,
      });
      setDiscussion(nextDiscussion);
      setDiscussionInput('');
      discussionMessageId.current = null;
      discussionPendingMessage.current = null;
      setConflict(false);
    } catch (requestError: any) {
      if (requestError?.response?.status === 409) {
        setConflict(true);
        setDiscussionError('The authoritative draft changed. Reload it before retrying this message.');
      } else {
        setDiscussionError('Unable to get a review assistant response. Try again.');
      }
    } finally {
      setDiscussionSending(false);
    }
  };

  const isStale = Boolean(plan && (
    plan.source_session_id !== sourceSessionId
    || JSON.stringify(plan.selected_source_ids) !== JSON.stringify(selectedSourceIds)
  ));
  const constraintsLocked = Boolean(plan);

  const groundingLabel = (metadata: UnitCondenserDiscussionMetadata) => {
    if (metadata.grounding_status === 'grounded') { return 'Grounded in plan evidence'; }
    if (metadata.grounding_status === 'partially_grounded') { return 'Partially grounded'; }
    return 'Insufficient evidence';
  };

  return (
    <div className="unit-condenser-plan-shell">
      <h3 id="unit-condenser-heading">{intl.formatMessage(messages.unitCondenserHeading)}</h3>
      <p>{intl.formatMessage(messages.unitCondenserDescription)}</p>
      {error && <Alert variant={conflict ? 'warning' : 'danger'}>{error}</Alert>}
      <nav aria-label="Unit Condenser steps">
        <ol className="ai-assisted-course-authoring-tab__wizard-steps">
          {STEPS.map((label, index) => (
            <li key={label} className={index === activeStep ? 'is-active' : ''}>
              <Button
                variant="tertiary"
                size="sm"
                disabled={!canNavigateTo(index)}
                aria-current={index === activeStep ? 'step' : undefined}
                onClick={() => goToStep(index)}
              >
                <span aria-hidden="true">{index + 1}.</span>
                <span>{label}</span>
              </Button>
            </li>
          ))}
        </ol>
      </nav>

      {activeStep === 0 && (
        <section aria-label="Analyze the source unit">
          <Form.Group controlId="unit-condenser-unit-url">
            <Form.Label>Learner-view unit URL</Form.Label>
            <Form.Control value={unitUrl} onChange={(event) => setUnitUrl(event.target.value)} disabled={saving} />
          </Form.Group>
          <div className="unit-condenser-plan-shell__actions">
            <Button onClick={analyze} disabled={analysisInProgress || !unitUrl.trim()} aria-busy={analysisInProgress}>
              {analysisInProgress && <Spinner animation="border" size="sm" />}<span>{analysisButtonLabel}</span>
            </Button>
          </div>
          {analysisStatus && analysisStatus !== 'completed' && <p aria-live="polite">Analysis status: {analysisStatus}</p>}
          {analysis && (
            <section className="unit-condenser-plan-shell__preflight-card" aria-live="polite">
              <h4>Unit preflight</h4>
              <dl>
                <div>
                  <dt>{analysis.word_count_status === 'validated' ? 'Instructional word count' : 'Estimated instructional word count'}</dt>
                  <dd><strong>{analysis.word_count.toLocaleString()}</strong> words</dd>
                </div>
                <div>
                  <dt>Assessment</dt>
                  <dd><strong>{analysis.assessment_status}</strong>
                    {analysis.assessment_question_count === null ? '' : ` (${analysis.assessment_question_count} questions)`}
                  </dd>
                </div>
                <div>
                  <dt>Images</dt>
                  <dd><strong>{analysis.image_count}</strong></dd>
                </div>
              </dl>
            </section>
          )}
        </section>
      )}

      {activeStep === 1 && (
        <section aria-label="Sources and constraints">
          <section aria-labelledby="unit-condenser-sources-heading">
            <h4 id="unit-condenser-sources-heading">Supplemental sources</h4>
            <p>Source files remain server-side and their contents are never shown in the browser.</p>
            <Form.Group controlId="unit-condenser-source-upload">
              <Form.Label>Upload DOCX, PDF, TXT, or Markdown</Form.Label>
              <Form.Control
                type="file"
                accept=".pdf,.docx,.txt,.md"
                multiple
                onChange={(event) => uploadSources(event.target.files)}
                disabled={constraintsLocked || uploadingSources || saving}
              />
            </Form.Group>
            {uploadingSources && (
            <div className="unit-condenser-plan-shell__upload-status" role="status">
              <Spinner animation="border" size="sm" /> Uploading sources...
            </div>
            )}
            {(uploadingSourceNames.length > 0
              || sources.some(source => selectedSourceIds.includes(source.source_id))) && (
              <ul className="unit-condenser-plan-shell__source-pills" aria-label="Uploaded supplemental sources">
                {uploadingSourceNames.map(filename => (
                  <li
                    key={`uploading-${filename}`}
                    className="unit-condenser-plan-shell__source-pill unit-condenser-plan-shell__source-pill--uploading"
                    title="loading..."
                    aria-busy="true"
                  >
                    <strong>{filename}</strong>
                    <span>loading...</span>
                  </li>
                ))}
                {sources
                  .filter(source => selectedSourceIds.includes(source.source_id))
                  .map(source => {
                    const sourceIsLoading = SOURCE_LOADING_STATUSES.includes(source.status);
                    const sourceName = source.filename || source.source_id;
                    return (
                      <li
                        key={source.source_id}
                        className={`unit-condenser-plan-shell__source-pill ${sourceIsLoading
                          ? 'unit-condenser-plan-shell__source-pill--uploading'
                          : 'unit-condenser-plan-shell__source-pill--selected'}`}
                        title={sourceIsLoading ? 'loading...' : undefined}
                        aria-busy={sourceIsLoading ? 'true' : undefined}
                      >
                        <strong>{sourceName}</strong>
                        {sourceIsLoading && <span>loading...</span>}
                        {!constraintsLocked && !sourceIsLoading && (
                        <IconButton
                          className="unit-condenser-plan-shell__source-pill-remove"
                          src={Close}
                          iconAs={Icon}
                          alt={`Remove ${sourceName}`}
                          onClick={() => { if (!saving) { removeSource(source.source_id); } }}
                          size="inline"
                        />
                        )}
                      </li>
                    );
                  })}
              </ul>
            )}
            {sourceProcessing && <Alert variant="warning">Selected sources can be used after processing completes.</Alert>}
          </section>
          <Form.Group controlId="unit-condenser-target">
            <Form.Label>Initial plan word budget</Form.Label>
            <Form.Control
              type="number"
              min={1}
              step={1}
              required
              value={target}
              isInvalid={Boolean(targetError)}
              aria-describedby={`unit-condenser-target-help${targetError ? ' unit-condenser-target-error' : ''}`}
              aria-invalid={targetError ? 'true' : undefined}
              onChange={(event) => {
                setTarget(event.target.value);
                setTargetError(null);
              }}
              disabled={constraintsLocked || saving}
            />
            <Form.Text id="unit-condenser-target-help">
              Enter a positive whole number greater than 0.
            </Form.Text>
            {targetError && (
              <Form.Text id="unit-condenser-target-error" className="text-danger" role="alert">
                {targetError}
              </Form.Text>
            )}
          </Form.Group>
          <Form.Group controlId="unit-condenser-instructions">
            <Form.Label>Additional instructions (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={instructions}
              onChange={(event) => setInstructions(event.target.value)}
              disabled={constraintsLocked || saving}
            />
          </Form.Group>
          {constraintsLocked && <Alert variant="info">A plan has been created. Review or edit its concepts in the next step.</Alert>}
          <div className="unit-condenser-plan-shell__actions">
            <Button
              onClick={plan ? () => goToStep(2) : create}
              disabled={saving || (!plan && (unavailableSelectedSources || !analysisJobId || analysisStatus !== 'completed' || !analysis || unitUrl.trim() !== analyzedUrl))}
              aria-busy={saving}
            >
              {saving && <Spinner animation="border" size="sm" />}<span>{plan ? 'Next: Review plan' : 'Create concept plan'}</span>
            </Button>
          </div>
        </section>
      )}

      {activeStep === 2 && plan && response && (
        <section aria-label="Review the concept plan">
          {recentGenerationContext && recentGenerationContext.condensation_suggestions.length > 0 && (
            <Alert
              variant="info"
              className="unit-condenser-plan-shell__recent-generation-context"
              aria-labelledby="unit-condenser-recent-generation-heading"
            >
              <h4 id="unit-condenser-recent-generation-heading">Suggestions from the previous generation</h4>
              <p>
                The previous attempt generated {recentGenerationContext.word_count} words against a target of{' '}
                {recentGenerationContext.target_word_count}. These suggestions remain available while you revise
                the plan.
              </p>
              <p>
                Assessment-required and assessment-supporting concepts are protected from removal suggestions.
              </p>
              <ul>
                {recentGenerationContext.condensation_suggestions.map(suggestion => (
                  <li key={`${suggestion.kind}-${suggestion.concept_ids.join('-')}`}>
                    <strong>{suggestion.concept_titles.join(', ')}</strong>: {suggestion.reason}{' '}
                    <span>Potential savings: {suggestion.estimated_word_savings} words.</span>
                  </li>
                ))}
              </ul>
            </Alert>
          )}
          <div className="unit-condenser-plan-shell__review-layout">
            <section
              className="unit-condenser-plan-shell__discussion"
              aria-labelledby="unit-condenser-discussion-heading"
            >
              <h4 id="unit-condenser-discussion-heading">Discuss this plan</h4>
              <p>
                The review assistant can explain tradeoffs and suggest edits. It does not change the plan by itself.
              </p>
              <div
                className="unit-condenser-plan-shell__discussion-log"
                role="log"
                aria-live="polite"
                aria-label="Unit Condenser discussion"
              >
                {(discussion?.messages || []).map(message => (
                  <div
                    key={message.id}
                    className={`unit-condenser-plan-shell__message unit-condenser-plan-shell__message--${message.role}`}
                  >
                    <strong>{message.role === 'author' ? 'You' : 'Review assistant'}:</strong>
                    <MarkdownMessage>{message.content}</MarkdownMessage>
                    {message.role === 'assistant' && message.metadata && (
                      <div
                        className="unit-condenser-plan-shell__message-quality"
                        role="status"
                        aria-label="Review assistant evidence status"
                      >
                        <p className={`unit-condenser-plan-shell__grounding-status unit-condenser-plan-shell__grounding-status--${message.metadata.grounding_status}`}>
                          <strong>{groundingLabel(message.metadata)}</strong>
                          {message.metadata.needs_clarification && ' · A focused clarification is needed.'}
                        </p>
                        {message.metadata.citations.length > 0 && (
                          <div>
                            <strong>Provenance</strong>
                            <ul aria-label="Evidence citations">
                              {message.metadata.citations.map((citation, index) => (
                                <li key={`${citation.kind}-${citation.source_id || citation.section_id || citation.concept_id || index}`}>
                                  {citation.concept_id ? (
                                    <a href={`#unit-condenser-concept-${citation.concept_id.replace(/[^a-zA-Z0-9_-]/g, '-')}`}>
                                      {citation.label}
                                    </a>
                                  ) : <span>{citation.label}</span>}: {citation.detail}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {message.metadata.context_metadata?.history_truncated && (
                          <p>Earlier discussion context was bounded; the complete transcript remains saved.</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <Form.Control
                as="textarea"
                rows={3}
                aria-label="Discussion message"
                value={discussionInput}
                onChange={(event) => setDiscussionInput(event.target.value)}
                disabled={discussionSending || discussionReloading || plan.state === 'locked' || saving}
              />
              <Button
                onClick={sendDiscussionMessage}
                disabled={discussionSending || discussionReloading || saving || plan.state === 'locked' || !discussionInput.trim()}
                aria-busy={discussionSending}
                style={{ marginBottom: '.5rem' }}
              >
                {discussionSending && <Spinner animation="border" size="sm" />}<span>Ask review assistant</span>
              </Button>
              {isDirty && plan.state === 'draft' && (
                <Alert variant="info">This question will be answered against your unsaved plan edits. Update Plan to persist them.</Alert>
              )}
              {discussionError && <Alert variant="warning">{discussionError}</Alert>}
              {conflict && (
                <Button
                  variant="outline-secondary"
                  onClick={reloadDiscussion}
                  disabled={discussionReloading}
                  aria-busy={discussionReloading}
                >
                  {discussionReloading && <Spinner animation="border" size="sm" />} Reload latest draft
                </Button>
              )}
            </section>
            <div className="unit-condenser-plan-shell__plan-editor">
              <h4>Proposed plan</h4>
              <p>Edit the actions and allocations on the right while you discuss the tradeoffs.</p>
              <p className="unit-condenser-plan-shell__planned-length" aria-live="polite">
                <span>Current planned length</span>
                <strong>{plan.proposed_word_count.toLocaleString()} words</strong>
              </p>
              <Form.Group controlId="unit-condenser-plan-instructions">
                <Form.Label>Additional instructions</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={plan.instructions}
                  disabled={plan.state === 'locked' || saving}
                  onChange={(event) => {
                    setInstructions(event.target.value);
                    setPlan({ ...plan, instructions: event.target.value });
                  }}
                />
              </Form.Group>
              <h4 className="unit-condenser-plan-shell__sections-heading">Main unit sections</h4>
              {plan.concepts.map((concept) => (
                <details
                  key={concept.id}
                  id={`unit-condenser-concept-${concept.id.replace(/[^a-zA-Z0-9_-]/g, '-')}`}
                  className="unit-condenser-plan-shell__concept"
                >
                  <summary>
                    <span className="unit-condenser-plan-shell__concept-title">{concept.title}</span>
                    <span className="unit-condenser-plan-shell__concept-action">{concept.action}</span>
                    <span
                      className="unit-condenser-plan-shell__concept-reasons"
                      role="list"
                      aria-label={`Why ${concept.title} matters`}
                    >
                      {conceptEvidence(concept).map((reason) => (
                        <span key={`${reason.kind}-${reason.label}`} role="listitem">
                          <strong>{reason.label}</strong>: {reason.detail}
                        </span>
                      ))}
                    </span>
                  </summary>
                  <div className="unit-condenser-plan-shell__concept-controls">
                    <p>Source section: {concept.source_section_ids.join(', ')}</p>
                    {concept.suggested_actions && concept.suggested_actions.length > 0 && (
                    <section
                      className="unit-condenser-plan-shell__suggested-actions"
                      aria-labelledby={`unit-condenser-suggested-actions-${concept.id}`}
                    >
                      <h5 id={`unit-condenser-suggested-actions-${concept.id}`}>Suggested Actions</h5>
                      <p>These are advisory recommendations based on the available evidence.</p>
                      <ul>
                        {concept.suggested_actions.map(suggestion => (
                          <li key={`${suggestion.kind}-${suggestion.target_concept_id || concept.id}`}>
                            <strong>{suggestion.label}</strong>: {suggestion.reason}
                            {suggestion.target_concept_title && (
                            <span> Suggested target: {suggestion.target_concept_title}.</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </section>
                    )}
                    <Form.Group controlId={`unit-condenser-action-${concept.id}`}>
                      <Form.Label>Plan action</Form.Label>
                      <Form.Control
                        as="select"
                        value={concept.action}
                        disabled={plan.state === 'locked' || saving}
                        onChange={(event) => {
                          const action = event.target.value as UnitCondenserPlanAction;
                          setProposedWordInputs(current => {
                            const next = { ...current };
                            delete next[concept.id];
                            return next;
                          });
                          let proposedWords = Math.min(concept.original_words, concept.proposed_words);
                          if (['remove', 'merge'].includes(action)) {
                            proposedWords = 0;
                          } else if (action === 'keep') {
                            proposedWords = concept.original_words;
                          }
                          updateConceptDetails(concept.id, {
                            action,
                            merge_target_id: action === 'merge' ? concept.merge_target_id : null,
                            proposed_words: proposedWords,
                          });
                        }}
                      >
                        <option value="keep">Keep unchanged</option>
                        <option value="shorten">Shorten</option>
                        <option value="merge">Merge</option>
                        <option value="remove">Remove</option>
                      </Form.Control>
                    </Form.Group>
                    {concept.action === 'merge' && (
                    <Form.Group controlId={`unit-condenser-merge-target-${concept.id}`}>
                      <Form.Label>Merge into</Form.Label>
                      <Form.Control
                        as="select"
                        value={concept.merge_target_id || ''}
                        disabled={plan.state === 'locked' || saving}
                        onChange={(event) => updateConceptDetails(concept.id, {
                          merge_target_id: event.target.value || null,
                        })}
                      >
                        <option value="">Choose a target</option>
                        {plan.concepts.filter(candidate => candidate.id !== concept.id).map(candidate => (
                          <option key={candidate.id} value={candidate.id}>{candidate.title}</option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                    )}
                    <Form.Group controlId={`unit-condenser-words-${concept.id}`}>
                      <Form.Label>Proposed words</Form.Label>
                      <Form.Control
                        type="number"
                        min={['keep', 'shorten'].includes(concept.action) ? 1 : 0}
                        max={['keep', 'shorten'].includes(concept.action) ? concept.original_words : undefined}
                        value={proposedWordInputs[concept.id] ?? String(concept.proposed_words)}
                        isInvalid={Boolean(proposedWordError(concept))}
                        aria-describedby={proposedWordError(concept)
                          ? `unit-condenser-words-${concept.id}-error` : undefined}
                        aria-invalid={proposedWordError(concept) ? 'true' : undefined}
                        disabled={plan.state === 'locked' || saving || ['keep', 'remove', 'merge'].includes(concept.action)}
                        onChange={(event) => {
                          const value = event.target.value;
                          const parsed = parsePositiveInteger(value);
                          if (parsed === null) {
                            setProposedWordInputs(current => ({ ...current, [concept.id]: value }));
                            return;
                          }
                          const proposedWords = Math.min(concept.original_words, parsed);
                          setProposedWordInputs(current => ({
                            ...current,
                            [concept.id]: String(proposedWords),
                          }));
                          updateConceptDetails(concept.id, { proposed_words: proposedWords });
                        }}
                      />
                      {proposedWordError(concept) && (
                        <Form.Text
                          id={`unit-condenser-words-${concept.id}-error`}
                          className="text-danger"
                          role="alert"
                        >
                          {proposedWordError(concept)}
                        </Form.Text>
                      )}
                      <div className="unit-condenser-plan-shell__concept-word-counts">
                        {conceptWordSummary(concept)}
                      </div>
                    </Form.Group>
                    <Form.Group controlId={`unit-condenser-rationale-${concept.id}`}>
                      <Form.Label>Rationale (optional)</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={concept.rationale}
                        disabled={plan.state === 'locked' || saving}
                        onChange={(event) => updateConceptDetails(concept.id, { rationale: event.target.value })}
                      />
                    </Form.Group>
                    {concept.assessment_importance === 'required' && (
                    <Form.Group controlId={`unit-condenser-override-${concept.id}`}>
                      <Form.Label>Author override rationale</Form.Label>
                      <Form.Control
                        value={concept.author_override || ''}
                        disabled={plan.state === 'locked' || saving}
                        onChange={(event) => updateConceptDetails(concept.id, {
                          author_override: event.target.value || null,
                        })}
                      />
                    </Form.Group>
                    )}
                  </div>
                </details>
              ))}
              {isStale && <Alert variant="warning">Source selections changed. Create a new draft revision before accepting this plan.</Alert>}
              {isDirty && plan.state === 'draft' && <Alert variant="warning">Update Plan before accepting these edits.</Alert>}
              <p>Revision {plan.revision} · {plan.state}</p>
              {revisions.length > 1 && (
              <section aria-labelledby="unit-condenser-revisions-heading">
                <h4 id="unit-condenser-revisions-heading">Revision history</h4>
                <ul>
                  {revisions.map(revision => <li key={`${revision.revision}-${revision.state}`}>Revision {revision.revision} · {revision.state}</li>)}
                </ul>
              </section>
              )}
              {plan.state === 'draft' && (
              <Button onClick={save} disabled={saving || isStale || hasInvalidProposedWordInput} aria-busy={saving}>
                {saving && <Spinner animation="border" size="sm" />}<span>Update Plan</span>
              </Button>
              )}
            </div>
          </div>
        </section>
      )}

      {activeStep === 3 && plan && response && (
        <section aria-label="Approve and generate">
          <p>
            Revision {plan.revision} · {plan.state} · {plan.proposed_word_count} words
          </p>
          <ul>
            {plan.concepts.map(concept => (
              <li key={concept.id} style={{ marginBottom: '0.5rem' }}>
                <strong>{concept.title}</strong>: {concept.action}, {approvalWordSummary(concept)}
              </li>
            ))}
          </ul>
          {plan.state === 'draft' && (
          <div className="unit-condenser-plan-shell__actions">
            <Button
              onClick={acceptAndGenerate}
              disabled={saving || isStale || isDirty || hasInvalidProposedWordInput}
              aria-busy={saving || generationPending}
            >
              {(saving || generationPending) && <Spinner animation="border" size="sm" />} Accept and generate unit
            </Button>
          </div>
          )}
          {plan.state === 'locked' && (
            <div className="unit-condenser-plan-shell__actions">
              <Button
                onClick={() => generate()}
                disabled={saving || generationPending}
                aria-busy={saving || generationPending}
              >
                {(saving || generationPending) && <Spinner animation="border" size="sm" />}<span>Generate condensed unit</span>
              </Button>
              <Button variant="outline-secondary" onClick={newDraft} disabled={saving}>Create new draft revision</Button>
            </div>
          )}
          {generationJob && <p aria-live="polite">Generation status: {generationJob.status}</p>}
          {generationPollingError && <Alert variant="warning">Unable to retrieve generation status. Polling will retry.</Alert>}
          {generationError && <Alert variant="danger">{generationError}</Alert>}
          {generationError && generationJob?.status === 'completed' && !generationResult && (
            <Button onClick={() => { setGenerationError(null); setGenerationResultRetry(current => current + 1); }}>
              Retry result
            </Button>
          )}
        </section>
      )}

      {activeStep === 4 && generationResult && (
        <UnitCondenserResult
          result={generationResult}
          onReset={reset}
          onAdjustPlan={newDraft}
          onRetry={generate}
        />
      )}

      <div className="ai-assisted-course-authoring-tab__unit-navigation">
        {activeStep > 0 && <Button variant="outline-secondary" onClick={() => setActiveStep(current => current - 1)}>Back</Button>}
        {activeStep === 0 && (
          <Button disabled={!canNavigateTo(1)} onClick={() => goToStep(1)}>
            Next: Sources &amp; constraints
          </Button>
        )}
        {activeStep === 1 && plan && <Button onClick={() => goToStep(2)}>Next: Review plan</Button>}
        {activeStep === 2 && plan && <Button disabled={isStale || hasInvalidProposedWordInput || (plan.state === 'draft' && (isDirty || !response))} onClick={() => goToStep(3)}>Next: Approve &amp; generate</Button>}
        {activeStep === 3 && generationResult && <Button onClick={() => goToStep(4)}>Next: Review result</Button>}
      </div>
    </div>
  );
};

export default UnitCondenserPlanShell;
